<template>
  <div class="abs-page">

    <!-- 頁首 -->
    <div class="abs-header">
      <h1 class="abs-title">店家服務管理</h1>
      <button class="abs-btn abs-btn--secondary abs-btn--sm"
        @click="router.push({ name: 'AdminBookingStores' })">
        ← 返回店家管理
      </button>
    </div>

    <!-- 從店家卡片進來：只顯示店家名稱 -->
    <div v-if="fromStore" class="abs-store-bar">
      <span class="abs-store-bar__name">{{ currentStoreName }}</span>
    </div>

    <!-- 直接進入頁面：下拉選擇 -->
    <div v-else class="abs-store-selector">
      <label>選擇店家</label>
      <select v-model="selectedStoreId" class="abs-select" @change="onStoreChange">
        <option value="">-- 請選擇店家 --</option>
        <option v-for="s in stores" :key="s.id" :value="s.id">
          {{ s.name }}（{{ s.region_name }}）
        </option>
      </select>
    </div>

    <!-- 尚未選店家 -->
    <div v-if="!fromStore && !selectedStoreId" class="abs-hint">請先選擇一家店家</div>

    <template v-else>

      <!-- Tab 切換 -->
      <div class="abs-tabs">
        <button
          class="abs-tab"
          :class="{ 'abs-tab--active': activeTab === 'services' }"
          @click="activeTab = 'services'"
        >服務項目</button>
        <button
          class="abs-tab"
          :class="{ 'abs-tab--active': activeTab === 'slots' }"
          @click="switchToSlots"
        >時段管理</button>
      </div>

      <!-- ══ Tab 1：服務項目 ═══════════════════════════ -->
      <div v-if="activeTab === 'services'">
        <div class="abs-tab-actions">
          <button class="abs-btn abs-btn--primary abs-btn--sm" @click="openCreate">
            ＋ 新增服務
          </button>
        </div>

        <div v-if="svcLoading" class="abs-state"><span class="abs-spinner" /> 載入中…</div>
        <div v-else-if="svcError" class="abs-alert--error">{{ svcError }}</div>
        <div v-else-if="services.length === 0" class="abs-empty">尚無服務項目，點上方「＋ 新增服務」建立</div>

        <div v-else class="abs-list">
          <div
            v-for="svc in services"
            :key="svc.id"
            class="abs-card"
            :class="{ 'abs-card--inactive': svc.status === 'inactive' }"
          >
            <div class="abs-card__info">
              <div class="abs-card__top">
                <span class="abs-svc-name">{{ svc.name }}</span>
                <span class="abs-badge" :class="svc.status === 'active' ? 'abs-badge--active' : 'abs-badge--inactive'">
                  {{ svc.status === 'active' ? '上架中' : '已停用' }}
                </span>
              </div>
              <p v-if="svc.description" class="abs-card__desc">{{ svc.description }}</p>
              <div class="abs-items-tags">
                <span v-for="it in (svc.service_items || [])" :key="it.id" class="abs-item-tag">
                  {{ it.name }}・{{ it.duration_minutes }}分・NT${{ Number(it.price).toLocaleString() }}
                </span>
              </div>
            </div>
            <div class="abs-card__actions">
              <button class="abs-btn abs-btn--sm abs-btn--slots" @click="openSlots(svc)">🕐 時段管理</button>
              <button class="abs-btn abs-btn--sm abs-btn--secondary" @click="openEdit(svc)">編輯</button>
              <button
                class="abs-btn abs-btn--sm"
                :class="svc.status === 'active' ? 'abs-btn--warn' : 'abs-btn--success'"
                :disabled="toggling[svc.id]"
                @click="toggleStatus(svc)"
              >{{ svc.status === 'active' ? '停用' : '啟用' }}</button>
              <button
                class="abs-btn abs-btn--sm abs-btn--danger"
                :disabled="svcDeleting[svc.id]"
                @click="deleteService(svc)"
              >刪除</button>
            </div>
            <p v-if="deleteErrors[svc.id]" class="abs-card__delete-err">{{ deleteErrors[svc.id] }}</p>
          </div>
        </div>
      </div>

      <!-- ══ Tab 2：時段管理 ═══════════════════════════ -->
      <div v-if="activeTab === 'slots'">

        <!-- 老師篩選 banner -->
        <div v-if="filterTeacher" class="abs-filter-bar">
          <span>🎯 目前只顯示「<strong>{{ filterTeacher }}</strong>」的時段</span>
          <button class="abs-filter-clear" @click="filterTeacher = ''">× 顯示全部老師</button>
        </div>

        <div class="abs-tab-actions">
          <button class="abs-btn abs-btn--primary abs-btn--sm" @click="openBatch">
            ＋ 批次建立時段
          </button>
          <button class="abs-btn abs-btn--warn abs-btn--sm" :disabled="migrating" @click="migrateSlots"
            title="將舊格式的跨日時段（24:00 / 25:00…）移到正確的隔天日期">
            <span v-if="migrating" class="abs-spinner abs-spinner--sm abs-spinner--white" />
            {{ migrating ? '修復中…' : '🔧 修復跨日時段' }}
          </button>
        </div>

        <!-- 月份導覽 -->
        <div class="abs-month-nav">
          <button class="abs-month-btn" @click="prevMonth">&#8592;</button>
          <span class="abs-month-label">{{ slotYearMonth }}</span>
          <button class="abs-month-btn" @click="nextMonth">&#8594;</button>
        </div>

        <!-- 日期快捷列 -->
        <div class="abs-day-strip">
          <button
            v-for="day in daysInMonth"
            :key="day.date"
            class="abs-day-chip"
            :class="{
              'abs-day-chip--selected': selectedDate === day.date,
              'abs-day-chip--today':    day.date === todayStr,
              'abs-day-chip--past':     day.date < todayStr,
            }"
            @click="selectDate(day.date)"
          >
            <span class="abs-day-chip__dow">{{ day.dow }}</span>
            <span class="abs-day-chip__num">{{ day.num }}</span>
          </button>
        </div>

        <!-- 選定日期的時段列表 -->
        <div class="abs-slots-area">
          <p v-if="!selectedDate" class="abs-slots-hint">請從上方選擇日期</p>

          <template v-else>
            <div class="abs-slots-date-row">
              <p class="abs-slots-date-label">{{ selectedDate }}</p>
              <button
                v-if="daySlots.length > 0"
                class="abs-btn abs-btn--sm abs-btn--danger"
                :disabled="holidaying"
                @click="markHoliday"
              >
                <span v-if="holidaying" class="abs-spinner abs-spinner--sm abs-spinner--white" />
                全天休假（清除 {{ daySlots.filter(s=>s.booked_count===0).length }} 個時段）
              </button>
            </div>

            <div v-if="slotsLoading" class="abs-state"><span class="abs-spinner" /> 載入中…</div>
            <div v-else-if="slotsError" class="abs-alert--error">{{ slotsError }}</div>

            <div v-else-if="filteredDaySlots.length === 0" class="abs-empty">
              {{ filterTeacher ? `「${filterTeacher}」在此日期無時段` : '此日期無時段（休假中），可點「＋ 批次建立時段」補回' }}
            </div>

            <div v-else class="abs-slot-list">
              <div
                v-for="sl in filteredDaySlots"
                :key="sl.id"
                class="abs-slot-row"
                :class="{ 'abs-slot-row--blocked': sl.status === 'blocked' }"
              >
                <span class="abs-slot-time">{{ formatSlotTime(sl.start_time) }} – {{ formatSlotTime(sl.end_time) }}</span>
                <span v-if="sl.teacher_name" class="abs-slot-teacher">{{ sl.teacher_name }}<template v-if="sl.service_item"> · {{ sl.service_item }}</template></span>
                <span class="abs-slot-cap">已預約 {{ sl.booked_count }}</span>
                <span class="abs-slot-status"
                  :class="sl.status === 'blocked' ? 'abs-slot-status--blocked'
                        : sl.booked_count >= sl.capacity ? 'abs-slot-status--full'
                        : 'abs-slot-status--ok'"
                >
                  {{ sl.status === 'blocked' ? '封鎖'
                   : sl.booked_count >= sl.capacity ? '已額滿'
                   : '可預約' }}
                </span>
                <button
                  class="abs-btn abs-btn--sm abs-btn--danger"
                  :disabled="deleting[sl.id]"
                  @click="deleteSlot(sl)"
                  :title="sl.booked_count > 0 ? `刪除（將取消 ${sl.booked_count} 筆預約）` : '刪除此時段'"
                >刪除</button>
              </div>
            </div>
          </template>
        </div>
      </div>

    </template>

    <!-- ══ Modal：新增 / 編輯服務 ══════════════════════ -->
    <teleport to="body">
      <div v-if="showSvcModal" class="abs-modal-overlay" @click.self="closeSvcModal">
        <div class="abs-modal" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">{{ editingId ? '編輯服務' : '新增服務' }}</h2>
            <button class="abs-modal__close" @click="closeSvcModal">✕</button>
          </div>
          <div class="abs-modal__body">
            <div class="abs-field">
              <label>老師名稱 <span class="abs-req">*</span></label>
              <input v-model.trim="svcForm.name" class="abs-input"
                :class="{ 'abs-input--error': svcErrors.name }" placeholder="例：王小美老師" />
              <p v-if="svcErrors.name" class="abs-field-err">{{ svcErrors.name }}</p>
            </div>

            <!-- 服務項目（多筆）-->
            <div class="abs-field">
              <label>服務項目 <span class="abs-req">*</span></label>
              <p v-if="svcErrors.service_items" class="abs-field-err">{{ svcErrors.service_items }}</p>
              <div class="abs-items-header">
                <span>項目名稱</span><span>時長（分鐘）</span><span>價格（NT$）</span><span></span>
              </div>
              <div v-for="(item, idx) in svcForm.service_items" :key="idx" class="abs-items-row">
                <input v-model.trim="item.name" type="text" placeholder="例：護膚" class="abs-input" />
                <input v-model.number="item.duration_minutes" type="number" min="1" max="480" placeholder="60" class="abs-input" />
                <input v-model.number="item.price" type="number" min="0" placeholder="0" class="abs-input" />
                <button v-if="svcForm.service_items.length > 1" type="button" class="abs-items-remove" @click="removeItem(idx)">✕</button>
              </div>
              <button type="button" class="abs-btn abs-btn--secondary abs-btn--sm" style="margin-top:4px" @click="addItem">
                ＋ 新增服務項目
              </button>
            </div>

            <div class="abs-field">
              <label>說明（選填）</label>
              <textarea v-model.trim="svcForm.description" class="abs-input abs-textarea" rows="3" />
            </div>
            <div class="abs-field" style="margin-top:14px">
              <label>排序（小的排前面）</label>
              <input v-model.number="svcForm.sort_order" type="number" min="0"
                class="abs-input abs-input--short" placeholder="0" />
            </div>
            <div v-if="editingId" class="abs-field">
              <label>狀態</label>
              <div class="abs-radio-group">
                <label class="abs-radio"><input type="radio" v-model="svcForm.status" value="active" /><span>上架中</span></label>
                <label class="abs-radio"><input type="radio" v-model="svcForm.status" value="inactive" /><span>停用</span></label>
              </div>
            </div>
            <p v-if="svcApiError" class="abs-api-error">{{ svcApiError }}</p>
          </div>
          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="closeSvcModal">取消</button>
            <button class="abs-btn abs-btn--primary" :disabled="submitting" @click="submitSvc">
              <span v-if="submitting" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ submitting ? '儲存中…' : (editingId ? '儲存變更' : '新增服務') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ══ Modal：批次建立時段 ══════════════════════════ -->
    <teleport to="body">
      <div v-if="showBatch" class="abs-modal-overlay" @click.self="closeBatch">
        <div class="abs-modal abs-modal--wide" role="dialog">
          <div class="abs-modal__header">
            <h2 class="abs-modal__title">批次建立時段</h2>
            <button class="abs-modal__close" @click="closeBatch">✕</button>
          </div>
          <div class="abs-modal__body">

            <div class="abs-field-row">
              <div class="abs-field abs-field--no-mb">
                <label>開始日期 <span class="abs-req">*</span></label>
                <input v-model="batchForm.start_date" type="date" :min="todayStr" class="abs-input"
                  :class="{ 'abs-input--error': batchErrors.start_date }" />
                <p v-if="batchErrors.start_date" class="abs-field-err">{{ batchErrors.start_date }}</p>
              </div>
              <div class="abs-field abs-field--no-mb">
                <label>結束日期 <span class="abs-req">*</span></label>
                <input v-model="batchForm.end_date" type="date" :min="batchForm.start_date || todayStr" class="abs-input"
                  :class="{ 'abs-input--error': batchErrors.end_date }" />
                <p v-if="batchErrors.end_date" class="abs-field-err">{{ batchErrors.end_date }}</p>
              </div>
            </div>

            <div class="abs-field">
              <label>每日時段設定 <span class="abs-req">*</span></label>
              <p v-if="batchErrors.slots" class="abs-field-err">{{ batchErrors.slots }}</p>

              <div class="abs-batch-schedule">
                <div class="abs-batch-row">
                  <div class="abs-batch-col">
                    <label>開始時間</label>
                    <input v-model="batchForm.day_start" type="time" class="abs-input" />
                  </div>
                  <div class="abs-batch-col">
                    <label>結束時間</label>
                    <div class="abs-time-next">
                      <input v-model="batchForm.day_end" type="time" class="abs-input" />
                      <label class="abs-checkbox-next">
                        <input type="checkbox" v-model="batchForm.next_day" />
                        <span>隔天</span>
                      </label>
                    </div>
                  </div>
                  <div class="abs-batch-col abs-batch-col--sm">
                    <label>間隔（分鐘）</label>
                    <select v-model.number="batchForm.interval" class="abs-input">
                      <option :value="30">30</option>
                      <option :value="60">60</option>
                      <option :value="90">90</option>
                      <option :value="120">120</option>
                    </select>
                  </div>
                </div>
                <p class="abs-batch-preview">
                  預覽：{{ batchPreview }}
                </p>
              </div>

              <!-- 老師由服務管理帶入，此處不需手動填寫 -->
              <p v-if="filterTeacher" class="abs-batch-teacher-hint">
                🧑‍💼 此時段將建立給：<strong>{{ filterTeacher }}</strong>
              </p>
            </div>

            <p v-if="batchApiError" class="abs-api-error">{{ batchApiError }}</p>

            <!-- 衝突提示 -->
            <div v-if="batchConflicts.length" class="abs-conflict-box">
              <p class="abs-conflict-title">⚠ 以下 {{ batchConflicts.length }} 個時段已存在，無法重複新增：</p>
              <ul class="abs-conflict-list">
                <li v-for="(c, i) in batchConflicts" :key="i">
                  {{ c.date }} &nbsp;
                  {{ c.start_time.slice(0,5) }} – {{ c.end_time.slice(0,5) }}
                  <span v-if="c.teacher_name" class="abs-conflict-cap">（{{ c.teacher_name }}）</span>
                </li>
              </ul>
              <label class="abs-duplicate-label">
                <input type="checkbox" v-model="batchForm.allow_duplicate" />
                <span>🛏️ 確認為 2 張（含）以上床位，允許相同時段重複新增</span>
              </label>
              <p class="abs-conflict-hint">
                💡 若要重新建立，請先刪除現有時段後再批次新增。
              </p>
            </div>
          </div>
          <div class="abs-modal__footer">
            <button class="abs-btn abs-btn--secondary" @click="closeBatch">取消</button>
            <button class="abs-btn abs-btn--primary" :disabled="batchSubmitting" @click="submitBatch">
              <span v-if="batchSubmitting" class="abs-spinner abs-spinner--sm abs-spinner--white" />
              {{ batchSubmitting ? '建立中…' : '建立時段' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route  = useRoute()

function handle401() { router.push({ name: 'AdminLogin' }) }

// ── 店家 ──────────────────────────────────────────────
const stores          = ref([])
const selectedStoreId = ref('')
const fromStore       = computed(() => !!route.query.storeId)
const currentStoreName = computed(() => {
  const s = stores.value.find(s => s.id === selectedStoreId.value)
  return s ? `${s.name}（${s.region_name}）` : ''
})

async function loadStores() {
  try {
    const res = await axios.get('/api/admin/booking/stores')
    stores.value = (res.data.stores || []).filter(s => s.status === 'active' || s.status === 'pending')
  } catch (e) {
    if (e.response?.status === 401) handle401()
  }
}

function onStoreChange() {
  services.value = []
  svcError.value = ''
  daySlots.value = []
  selectedDate.value = ''
  if (selectedStoreId.value) loadServices()
}

// ── Tab ───────────────────────────────────────────────
const activeTab = ref('services')

function switchToSlots() {
  activeTab.value = 'slots'
  if (selectedDate.value) loadDaySlots()
}

// ══════════════════════════════════════════════════════
//  Tab 1：服務項目
// ══════════════════════════════════════════════════════
const services   = ref([])
const svcLoading = ref(false)
const svcError   = ref('')
const toggling     = ref({})
const svcDeleting    = ref({})
const deleteErrors   = ref({})

async function loadServices() {
  if (!selectedStoreId.value) return
  svcLoading.value = true
  svcError.value   = ''
  try {
    const res = await axios.get(`/api/admin/booking/stores/${selectedStoreId.value}/services`)
    services.value = res.data.services || []
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    svcError.value = e.response?.data?.message || '載入失敗'
  } finally {
    svcLoading.value = false
  }
}

// Modal
const showSvcModal = ref(false)
const editingId    = ref(null)
const submitting   = ref(false)
const svcApiError  = ref('')

const emptyItem    = () => ({ name: '', duration_minutes: 60, price: 0 })
const emptySvcForm = () => ({
  name: '', description: '', sort_order: 0, status: 'active',
  service_items: [emptyItem()],
})

function addItem()       { svcForm.value.service_items.push(emptyItem()) }
function removeItem(idx) { if (svcForm.value.service_items.length > 1) svcForm.value.service_items.splice(idx, 1) }
const svcForm   = ref(emptySvcForm())
const svcErrors = ref({})

function openCreate() {
  editingId.value   = null
  svcForm.value     = emptySvcForm()
  svcErrors.value   = {}
  svcApiError.value = ''
  showSvcModal.value = true
}
function openEdit(svc) {
  editingId.value = svc.id
  svcForm.value = {
    name: svc.name, description: svc.description || '',
    sort_order: svc.sort_order ?? 0, status: svc.status,
    service_items: (svc.service_items && svc.service_items.length)
      ? svc.service_items.map(it => ({ name: it.name, duration_minutes: it.duration_minutes, price: it.price }))
      : [emptyItem()],
  }
  svcErrors.value   = {}
  svcApiError.value = ''
  showSvcModal.value = true
}
function closeSvcModal() { showSvcModal.value = false }

function validateSvc() {
  const e = {}
  if (!svcForm.value.name) e.name = '請填寫老師名稱'
  if (svcForm.value.service_items.every(it => !it.name?.trim())) e.service_items = '請至少填寫一個服務項目名稱'
  svcErrors.value = e
  return Object.keys(e).length === 0
}

async function submitSvc() {
  if (!validateSvc()) return
  submitting.value  = true
  svcApiError.value = ''
  const payload = {
    name: svcForm.value.name, description: svcForm.value.description || null,
    sort_order: svcForm.value.sort_order ?? 0, status: svcForm.value.status,
    service_items: svcForm.value.service_items.filter(it => it.name?.trim()),
  }
  try {
    if (editingId.value) {
      await axios.put(`/api/admin/booking/stores/${selectedStoreId.value}/services/${editingId.value}`, payload)
    } else {
      await axios.post(`/api/admin/booking/stores/${selectedStoreId.value}/services`, payload)
    }
    closeSvcModal()
    await loadServices()
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    svcApiError.value = e.response?.data?.message || '儲存失敗，請稍後再試'
  } finally {
    submitting.value = false
  }
}

async function deleteService(svc) {
  deleteErrors.value[svc.id] = ''
  if (svc.status === 'active') {
    deleteErrors.value[svc.id] = '請先停用此服務，再進行刪除'
    return
  }
  if (!confirm(`確定要刪除「${svc.name}」嗎？此操作無法復原。`)) return
  svcDeleting.value[svc.id] = true
  try {
    await axios.delete(`/api/admin/booking/stores/${selectedStoreId.value}/services/${svc.id}`)
    services.value = services.value.filter(s => s.id !== svc.id)
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    deleteErrors.value[svc.id] = e.response?.data?.message || '刪除失敗，請稍後再試'
  } finally {
    svcDeleting.value[svc.id] = false
  }
}

async function toggleStatus(svc) {
  toggling.value[svc.id] = true
  const newStatus = svc.status === 'active' ? 'inactive' : 'active'
  try {
    await axios.put(`/api/admin/booking/stores/${selectedStoreId.value}/services/${svc.id}`, {
      name: svc.name, description: svc.description,
      sort_order: svc.sort_order, status: newStatus,
      service_items: svc.service_items || [],
    })
    svc.status = newStatus
  } catch (e) {
    if (e.response?.status === 401) handle401()
  } finally {
    toggling.value[svc.id] = false
  }
}

// ══════════════════════════════════════════════════════
//  Tab 2：時段管理
// ══════════════════════════════════════════════════════
const todayStr     = new Date().toLocaleDateString('sv-SE') // YYYY-MM-DD

// 老師篩選
const filterTeacher = ref('')

function openSlots(svc) {
  filterTeacher.value = svc.name
  activeTab.value     = 'slots'
  if (selectedDate.value) loadDaySlots()
}

const filteredDaySlots = computed(() => {
  if (!filterTeacher.value) return daySlots.value
  return daySlots.value.filter(s => s.teacher_name === filterTeacher.value)
})
const slotYear     = ref(new Date().getFullYear())
const slotMonth    = ref(new Date().getMonth())  // 0-11
const selectedDate = ref('')
const daySlots     = ref([])
const slotsLoading = ref(false)
const slotsError   = ref('')
const deleting     = ref({})

const slotYearMonth = computed(() =>
  `${slotYear.value} 年 ${slotMonth.value + 1} 月`
)

const daysInMonth = computed(() => {
  const days = []
  const total = new Date(slotYear.value, slotMonth.value + 1, 0).getDate()
  const DOW = ['日', '一', '二', '三', '四', '五', '六']
  for (let d = 1; d <= total; d++) {
    const dt  = new Date(slotYear.value, slotMonth.value, d)
    const str = dt.toLocaleDateString('sv-SE')
    days.push({ date: str, dow: DOW[dt.getDay()], num: d })
  }
  return days
})

function prevMonth() {
  if (slotMonth.value === 0) { slotYear.value--; slotMonth.value = 11 }
  else slotMonth.value--
  selectedDate.value = ''
  daySlots.value = []
}
function nextMonth() {
  if (slotMonth.value === 11) { slotYear.value++; slotMonth.value = 0 }
  else slotMonth.value++
  selectedDate.value = ''
  daySlots.value = []
}

function selectDate(date) {
  selectedDate.value = date
  loadDaySlots()
}

async function loadDaySlots() {
  if (!selectedStoreId.value || !selectedDate.value) return
  slotsLoading.value = true
  slotsError.value   = ''
  try {
    const res = await axios.get(
      `/api/admin/booking/stores/${selectedStoreId.value}/slots`,
      { params: { date: selectedDate.value } }
    )
    daySlots.value = res.data.slots || []
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    slotsError.value = e.response?.data?.message || '載入失敗'
  } finally {
    slotsLoading.value = false
  }
}

async function deleteSlot(sl) {
  const timeLabel = `${formatSlotTime(sl.start_time)}–${formatSlotTime(sl.end_time)}`
  let msg = `確定刪除 ${timeLabel} 這個時段？`
  if (sl.booked_count > 0) {
    msg += `\n\n⚠ 此時段有 ${sl.booked_count} 筆預約，刪除後相關預約將一併取消。`
  }
  if (!confirm(msg)) return
  deleting.value[sl.id] = true
  try {
    const res = await axios.delete(`/api/admin/booking/stores/${selectedStoreId.value}/slots/${sl.id}`)
    daySlots.value = daySlots.value.filter(s => s.id !== sl.id)
    if (res.data.cancelledCount > 0) {
      alert(`時段已刪除，同時取消了 ${res.data.cancelledCount} 筆預約。`)
    }
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '刪除失敗')
  } finally {
    deleting.value[sl.id] = false
  }
}

// 全天休假
const holidaying = ref(false)
async function markHoliday() {
  const canDelete = daySlots.value.filter(s => s.booked_count === 0).length
  const hasBooked = daySlots.value.filter(s => s.booked_count > 0).length
  let msg = `確定將 ${selectedDate.value} 設為休假？將清除 ${canDelete} 個時段。`
  if (hasBooked) msg += `\n（${hasBooked} 個已有預約的時段不會被刪除）`
  if (!confirm(msg)) return
  holidaying.value = true
  try {
    const res = await axios.delete(
      `/api/admin/booking/stores/${selectedStoreId.value}/slots/day/${selectedDate.value}`
    )
    alert(`已清除 ${res.data.deleted} 個時段`)
    await loadDaySlots()
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '操作失敗')
  } finally {
    holidaying.value = false
  }
}

// 一次性修復：將 start_time ≥ 24:00 的舊格式時段移到正確的隔天
const migrating = ref(false)
async function migrateSlots() {
  if (!confirm(
    '將所有店家中，時間格式為 24:00 / 25:00… 的舊時段，' +
    '自動移到正確的隔天日期（例：4/8 的 25:00 → 4/9 的 01:00）。\n\n確定執行？'
  )) return
  migrating.value = true
  try {
    const res = await axios.post('/api/admin/booking/migrate-slots')
    alert(res.data.message || '修復完成')
    // 若目前有選取日期，重新載入
    if (selectedDate.value) await loadDaySlots()
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '修復失敗，請稍後再試')
  } finally {
    migrating.value = false
  }
}

// 批次建立
const showBatch        = ref(false)
const batchSubmitting  = ref(false)
const batchApiError    = ref('')
const batchErrors      = ref({})
const batchConflicts   = ref([])

const emptyBatchForm = () => ({
  start_date:      selectedDate.value || todayStr,
  end_date:        selectedDate.value || todayStr,
  day_start:       '09:00',
  day_end:         '18:00',
  next_day:        false,
  interval:        60,
  allow_duplicate: false,  // 多床位模式：允許相同時段重複新增
})
const batchForm = ref(emptyBatchForm())

// 分鐘數 → 時間字串（支援超過 24h，例如 1530min → '25:30'）
function minsToTimeStr(mins) {
  return `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`
}

// 顯示用：'25:30:00' → '隔天 01:30'
function formatSlotTime(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  if (h >= 24) return `隔天 ${String(h-24).padStart(2,'0')}:${String(m).padStart(2,'0')}`
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`
}

// 產生時段陣列（支援跨天）
function generateSlots(dayStart, dayEnd, interval, nextDay = false) {
  if (!dayStart || !dayEnd || !interval) return []
  const [sh, sm] = dayStart.split(':').map(Number)
  const [eh, em] = dayEnd.split(':').map(Number)
  let cur = sh * 60 + sm
  const end = eh * 60 + em + (nextDay ? 24 * 60 : 0)
  if (end <= cur) return []
  const slots = []
  while (cur + interval <= end) {
    const s = minsToTimeStr(cur)
    cur += interval
    slots.push({ start_time: s, end_time: minsToTimeStr(cur) })
  }
  return slots
}

// 預覽
const batchPreview = computed(() => {
  const { day_start, day_end, interval, next_day } = batchForm.value
  if (!day_start || !day_end || !interval) return '請填寫時間'
  const slots = generateSlots(day_start, day_end, interval, next_day)
  if (slots.length === 0) return '時間設定有誤'
  const endLabel = next_day ? `（隔天 ${day_end}）` : day_end
  return `${day_start} 至 ${endLabel}，共 ${slots.length} 個時段`
})

function openBatch() {
  batchForm.value      = emptyBatchForm()
  batchErrors.value    = {}
  batchApiError.value  = ''
  batchConflicts.value = []
  showBatch.value      = true
}
function closeBatch() { showBatch.value = false }

function validateBatch() {
  const e = {}
  if (!batchForm.value.start_date) e.start_date = '請填寫開始日期'
  if (!batchForm.value.end_date)   e.end_date   = '請填寫結束日期'
  if (batchForm.value.start_date && batchForm.value.end_date &&
      batchForm.value.end_date < batchForm.value.start_date) {
    e.end_date = '結束日期不能早於開始日期'
  }
  const slots = generateSlots(batchForm.value.day_start, batchForm.value.day_end, batchForm.value.interval, batchForm.value.next_day)
  if (slots.length === 0) e.slots = '時間設定有誤，請確認結束時間晚於開始時間（或勾選隔天）'
  batchErrors.value = e
  return Object.keys(e).length === 0
}

async function submitBatch() {
  if (!validateBatch()) return
  batchSubmitting.value  = true
  batchApiError.value    = ''
  batchConflicts.value   = []
  // 老師由 filterTeacher（從服務管理帶入）自動設定，未指定則為 null
  const timeslots = generateSlots(batchForm.value.day_start, batchForm.value.day_end, batchForm.value.interval, batchForm.value.next_day)
  const slots = timeslots.map(ts => ({
    ...ts,
    teacher_name: filterTeacher.value || null,
    service_item: null,
  }))
  try {
    const res = await axios.post(
      `/api/admin/booking/stores/${selectedStoreId.value}/slots/batch`,
      { start_date: batchForm.value.start_date, end_date: batchForm.value.end_date, slots, allow_duplicate: batchForm.value.allow_duplicate || false }
    )
    closeBatch()
    alert(`成功建立 ${res.data.inserted} 個時段`)
    if (selectedDate.value) loadDaySlots()
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    if (e.response?.status === 409 && e.response.data?.conflicts?.length) {
      batchConflicts.value = e.response.data.conflicts
    } else {
      batchApiError.value = e.response?.data?.message || '建立失敗，請稍後再試'
    }
  } finally {
    batchSubmitting.value = false
  }
}

// ── 初始化 ────────────────────────────────────────────
onMounted(async () => {
  await loadStores()
  if (route.query.storeId) {
    selectedStoreId.value = Number(route.query.storeId)
    loadServices()
  }
})
</script>

<style scoped>
.abs-page { padding: 24px; max-width: 900px; }

/* 頁首 */
.abs-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;
}
.abs-title { font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin: 0; }

/* 店家列 */
.abs-store-bar {
  display: flex; align-items: center; gap: 12px; margin-bottom: 0;
  background: #f4f6f9; border-radius: 10px 10px 0 0; padding: 14px 18px;
  border-bottom: 1px solid #e0e6ed;
}
.abs-store-bar__name { font-size: 1rem; font-weight: 600; color: #2c3e50; }
.abs-store-selector {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  margin-bottom: 0; background: #f4f6f9;
  border-radius: 10px 10px 0 0; padding: 14px 18px;
  border-bottom: 1px solid #e0e6ed;
}
.abs-store-selector label { font-size: 0.88rem; color: #555; font-weight: 500; }
.abs-select {
  font-size: 0.92rem; padding: 8px 12px;
  border: 1px solid #dce1e7; border-radius: 7px; background: #fff;
  min-width: 220px; flex: 1;
}

/* Tab */
.abs-tabs {
  display: flex; border-bottom: 2px solid #e0e6ed; margin-bottom: 20px;
  background: #f4f6f9; padding: 0 18px;
}
.abs-tab {
  padding: 12px 20px; font-size: 0.92rem; font-weight: 500;
  border: none; background: none; cursor: pointer;
  color: #888; border-bottom: 2px solid transparent; margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}
.abs-tab--active { color: #2c3e50; border-bottom-color: #2c3e50; font-weight: 700; }
.abs-tab:hover:not(.abs-tab--active) { color: #555; }

/* Tab 動作列 */
.abs-tab-actions { display: flex; justify-content: flex-end; margin-bottom: 14px; }

/* 狀態 */
.abs-hint  { text-align: center; color: #aaa; padding: 48px 0; font-size: 0.95rem; }
.abs-empty { text-align: center; color: #aaa; padding: 32px 0; font-size: 0.9rem; }
.abs-state { display: flex; align-items: center; gap: 10px; color: #aaa; padding: 32px 0; }
.abs-alert--error {
  background: #fdedec; border: 1px solid #f5b7b1; border-radius: 8px;
  padding: 12px 16px; color: #c0392b; font-size: 0.9rem; margin-bottom: 12px;
}

/* 服務列表 */
.abs-list { display: flex; flex-direction: column; gap: 10px; }
.abs-card {
  display: flex; align-items: flex-start; gap: 16px; flex-wrap: wrap;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
  padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); transition: opacity 0.2s;
}
.abs-card--inactive { opacity: 0.55; }
.abs-card__info { flex: 1; min-width: 0; }
.abs-card__top  { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 5px; }
.abs-svc-name   { font-size: 1rem; font-weight: 600; color: #2c3e50; }
.abs-card__desc { font-size: 0.87rem; color: #888; margin: 0 0 8px; }
.abs-card__meta { display: flex; gap: 16px; font-size: 0.87rem; color: #8b6f5c; font-weight: 500; }
.abs-card__actions { display: flex; gap: 8px; flex-shrink: 0; }
.abs-card__delete-err { font-size: 0.82rem; color: #e74c3c; margin: 4px 0 0; width: 100%; }

.abs-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
.abs-badge--active   { background: #d4edda; color: #155724; }
.abs-badge--inactive { background: #e9ecef; color: #6c757d; }

/* ── 時段管理 ─────────────────────────────────── */
.abs-month-nav {
  display: flex; align-items: center; gap: 16px; margin-bottom: 12px;
}
.abs-month-btn {
  background: none; border: 1px solid #dce1e7; border-radius: 6px;
  width: 32px; height: 32px; cursor: pointer; font-size: 1rem;
  display: flex; align-items: center; justify-content: center;
}
.abs-month-btn:hover { background: #f4f6f9; }
.abs-month-label { font-size: 1rem; font-weight: 600; color: #2c3e50; }

.abs-day-strip {
  display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 20px;
}
.abs-day-chip {
  display: flex; flex-direction: column; align-items: center;
  width: 44px; padding: 6px 4px; border-radius: 8px; border: 1px solid #e0e6ed;
  background: #fff; cursor: pointer; font-size: 0.78rem;
  transition: all 0.15s;
}
.abs-day-chip:hover { border-color: #2c3e50; }
.abs-day-chip--selected { background: #2c3e50; color: #fff; border-color: #2c3e50; }
.abs-day-chip--today    { border-color: #8b6f5c; color: #8b6f5c; font-weight: 700; }
.abs-day-chip--today.abs-day-chip--selected { color: #fff; }
.abs-day-chip--past     { opacity: 0.4; }
.abs-day-chip__dow { font-size: 0.7rem; margin-bottom: 2px; }
.abs-day-chip__num { font-size: 0.88rem; font-weight: 600; }

.abs-slots-area  { min-height: 80px; }
.abs-slots-hint  { color: #bbb; font-size: 0.9rem; padding: 16px 0; }
.abs-slots-date-label { font-size: 0.9rem; font-weight: 600; color: #555; margin-bottom: 12px; }

.abs-slot-list { display: flex; flex-direction: column; gap: 8px; }
.abs-slot-row {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 8px; padding: 10px 14px;
}
.abs-slot-row--blocked { opacity: 0.55; }
.abs-slot-time    { font-size: 0.95rem; font-weight: 600; color: #2c3e50; min-width: 110px; }
.abs-slot-teacher { font-size: 0.85rem; color: #5d6d7e; flex: 1; }
.abs-slot-cap     { font-size: 0.85rem; color: #888; white-space: nowrap; }

/* 老師列 */
.abs-teacher-row   { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; }
.abs-teacher-input { flex: 1; }
.abs-teacher-remove { flex: 0 0 auto; }
.abs-slot-status { font-size: 0.78rem; padding: 2px 8px; border-radius: 12px; font-weight: 600; }
.abs-slot-status--ok      { background: #d4edda; color: #155724; }
.abs-slot-status--full    { background: #fff3cd; color: #856404; }
.abs-slot-status--blocked { background: #e9ecef; color: #6c757d; }

/* 批次建立時段 entry */
.abs-slot-entry {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: 10px;
}
.abs-slot-entry__dash      { color: #888; }
.abs-slot-entry__cap-label { font-size: 0.85rem; color: #555; white-space: nowrap; }
.abs-input--time { width: 120px; flex-shrink: 0; }
.abs-input--cap  { width: 70px; flex-shrink: 0; }
.abs-slot-add-btn { margin-top: 4px; }

/* 隔天 checkbox */
.abs-time-next {
  display: flex; align-items: center; gap: 10px;
}
.abs-checkbox-next {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.88rem; color: #555; cursor: pointer; white-space: nowrap;
}

/* 按鈕 */
.abs-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 16px; border-radius: 7px; border: none;
  font-size: 0.9rem; font-weight: 500; cursor: pointer;
  transition: background 0.15s; white-space: nowrap;
}
.abs-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.abs-btn--sm        { padding: 6px 12px; font-size: 0.82rem; }
.abs-btn--primary   { background: #2c3e50; color: #fff; }
.abs-btn--primary:hover:not(:disabled) { background: #34495e; }
.abs-btn--secondary { background: #fff; color: #2c3e50; border: 1px solid #dce1e7; }
.abs-btn--secondary:hover:not(:disabled) { background: #f4f6f9; }
.abs-btn--warn    { background: transparent; border: 1px solid #e67e22; color: #e67e22; }
.abs-btn--warn:hover:not(:disabled)    { background: #fef5ec; }
.abs-btn--success { background: #27ae60; color: #fff; }
.abs-btn--success:hover:not(:disabled) { background: #229954; }
.abs-btn--danger  { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.abs-btn--danger:hover:not(:disabled)  { background: #fdedec; }
/* 服務項目標籤 */
.abs-items-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.abs-item-tag {
  font-size: 0.78rem; color: #5d4037;
  background: #fdf3ee; border: 1px solid #f0d5c4;
  border-radius: 20px; padding: 2px 10px;
}
/* 服務項目動態列表（Modal 內）*/
.abs-items-header {
  display: grid; grid-template-columns: 2fr 1fr 1fr 32px;
  gap: 8px; margin-bottom: 4px; font-size: 0.78rem; color: #888;
}
.abs-items-row {
  display: grid; grid-template-columns: 2fr 1fr 1fr 32px;
  gap: 8px; margin-bottom: 8px; align-items: center;
}
.abs-items-remove {
  background: none; border: none; color: #aaa; cursor: pointer;
  font-size: 1rem; padding: 4px; line-height: 1; border-radius: 4px;
}
.abs-items-remove:hover { color: #e74c3c; background: #fdedec; }

.abs-btn--slots { background: #eaf3fb; color: #2471a3; border: 1px solid #aed6f1; }
.abs-btn--slots:hover:not(:disabled) { background: #d6eaf8; }

/* 老師篩選 banner */
.abs-filter-bar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
  background: #eaf3fb; border: 1px solid #aed6f1; border-radius: 8px;
  padding: 10px 14px; margin-bottom: 12px; font-size: 0.88rem; color: #1a5276;
}
.abs-filter-clear {
  background: none; border: 1px solid #aed6f1; border-radius: 6px;
  padding: 4px 10px; font-size: 0.82rem; color: #2471a3; cursor: pointer;
  white-space: nowrap;
}
.abs-filter-clear:hover { background: #d6eaf8; }

/* Spinner */
.abs-spinner {
  display: inline-block; width: 22px; height: 22px;
  border: 3px solid rgba(0,0,0,0.1); border-top-color: #7f8c8d;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
.abs-spinner--sm    { width: 14px; height: 14px; border-width: 2px; }
.abs-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Modal */
.abs-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  z-index: 600; display: flex; align-items: flex-start;
  justify-content: center; padding: 16px; overflow-y: auto;
}
.abs-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 520px;
  margin: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18);
}
.abs-modal--wide { max-width: 640px; }
.abs-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px 14px; border-bottom: 1px solid #e9ecef;
  position: sticky; top: 0; background: #fff; z-index: 1; border-radius: 14px 14px 0 0;
}
.abs-modal__title { font-size: 1.05rem; font-weight: 600; color: #2c3e50; margin: 0; }
.abs-modal__close {
  background: none; border: none; font-size: 1.1rem; cursor: pointer;
  color: #aaa; padding: 4px 8px; border-radius: 4px;
}
.abs-modal__close:hover { background: #f4f6f9; color: #555; }
.abs-modal__body   { padding: 20px 24px; display: flex; flex-direction: column; gap: 0; }
.abs-modal__footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 24px 20px; border-top: 1px solid #e9ecef; flex-wrap: wrap;
}

/* 表單 */
.abs-field       { margin-bottom: 14px; }
.abs-field--no-mb { margin-bottom: 0; }
.abs-field label {
  display: block; font-size: 0.87rem; font-weight: 500; color: #555; margin-bottom: 5px;
}
.abs-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
.abs-req         { color: #e74c3c; }
.abs-section-hint { font-size: 0.78rem; color: #999; font-weight: 400; margin-left: 6px; }
.abs-input {
  width: 100%; padding: 9px 12px; border: 1px solid #dce1e7; border-radius: 8px;
  font-size: 0.92rem; background: #fff; box-sizing: border-box;
  transition: border-color 0.15s; -webkit-appearance: none;
}
.abs-input:focus { outline: none; border-color: #2c3e50; box-shadow: 0 0 0 3px rgba(44,62,80,0.07); }
.abs-input--error { border-color: #e74c3c; }
.abs-input--short { max-width: 100px; }
.abs-textarea     { resize: vertical; min-height: 72px; }
.abs-field-err    { font-size: 0.8rem; color: #e74c3c; margin: 3px 0 0; }
.abs-radio-group  { display: flex; gap: 20px; }
.abs-radio        { display: flex; align-items: center; gap: 6px; font-size: 0.9rem; cursor: pointer; }
.abs-api-error {
  background: #fdedec; border: 1px solid #f5b7b1; border-radius: 7px;
  padding: 10px 14px; font-size: 0.87rem; color: #c0392b; margin-top: 8px;
}

.abs-batch-teacher-hint {
  margin: 12px 0 0;
  font-size: 0.85rem;
  color: #555;
  background: #f0f7ff;
  border: 1px solid #c5dff8;
  border-radius: 6px;
  padding: 8px 12px;
}

/* 衝突提示框 */
.abs-conflict-box {
  margin-top: 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.87rem;
  color: #5d4037;
}
.abs-conflict-title { font-weight: 600; margin: 0 0 8px; }
.abs-conflict-list  { margin: 0 0 10px; padding-left: 18px; line-height: 1.9; }
.abs-conflict-cap   { color: #e65100; font-weight: 500; }
.abs-duplicate-label {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.85rem; color: #4e342e; cursor: pointer;
  margin: 6px 0 10px;
}
.abs-conflict-hint  {
  margin: 0; font-size: 0.83rem; color: #6d4c41; line-height: 1.6;
  border-top: 1px solid #ffe082; padding-top: 8px;
}

@media (max-width: 600px) {
  .abs-page { padding: 14px; }
  .abs-field-row { grid-template-columns: 1fr; }
  .abs-card { flex-direction: column; }
  .abs-slot-entry { flex-direction: column; align-items: flex-start; }
  .abs-input--time, .abs-input--cap { width: 100%; }
  .abs-modal__footer { flex-direction: column-reverse; }
  .abs-modal__footer .abs-btn { width: 100%; justify-content: center; }
}
</style>
